import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import AuthPageLayout from "../../components/auth/AuthPageLayout";
import LoginSideImage from '../../images/login/login-side-forgot.png';
import OTPInput from "react-otp-input";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context/AuthContext";
import Fetch from "../../services/Fetch";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";

const loginInputSx = {
    width: '100%',
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.85)' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#fff' },
    '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255,255,255,0.5)' },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#fff' },
    '& .MuiInput-underline:after': { borderBottomColor: '#fff' },
    '& .MuiInputBase-input': { color: '#fff' },
};

function ForgotPassword() {
    const host = `${process.env.REACT_APP_LOCAL_HOST}`;
    const navigate = useNavigate();
    const { wait } = useContext(AuthContext);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { sendWait, setSendWait } = useWaits();
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');

    const forgotPassword = async () => {
        if (!email.trim()) {
            setSnackBar('error', 'أدخل البريد الإلكتروني');
            return;
        }

        setSendWait(true);
        const formData = new FormData();
        formData.append('email', email.trim());
        formData.append('account_role', 'student');

        const result = await Fetch(host + '/forget-password', 'POST', formData);

        if (result.status === 200) {
            setSnackBar('success', 'تم إرسال رمز التحقق إلى بريدك');
            setStep(2);
        } else if (result.status === 422) {
            setSnackBar('error', result.data?.errors?.[0] || 'تعذر إرسال الرمز');
        }

        setSendWait(false);
    };

    const resetPassword = async () => {
        if (code.length < 6) {
            setSnackBar('error', 'أدخل رمز التحقق المكوّن من 6 أرقام');
            return;
        }
        if (password.length < 6) {
            setSnackBar('error', 'كلمة المرور يجب أن تكون 6 أحرف على الأقل');
            return;
        }
        if (password !== passwordConfirmation) {
            setSnackBar('error', 'كلمتا المرور غير متطابقتين');
            return;
        }

        setSendWait(true);
        const formData = new FormData();
        formData.append('email', email.trim());
        formData.append('account_role', 'student');
        formData.append('code', code);
        formData.append('password', password);
        formData.append('password_confirmation', passwordConfirmation);

        const result = await Fetch(host + '/forget-password/check-code', 'POST', formData);

        if (result.status === 200) {
            setSnackBar('success', 'تم تحديث كلمة المرور بنجاح');
            navigate('/login');
        } else if (result.status === 422) {
            setSnackBar('error', result.data?.errors?.[0] || 'تعذر تحديث كلمة المرور');
        }

        setSendWait(false);
    };

    return (
        <>
            {wait ? (
                <Box className="w-full h-screen relative flex justify-center items-center">
                    <CircularProgress size={70} />
                </Box>
            ) : (
                <AuthPageLayout
                    sideImage={LoginSideImage}
                    sideTitle="استعد الوصول إلى حسابك"
                    sideSubtitle="أدخل بريدك الإلكتروني وسنرسل لك رمز تحقق آمن لإعادة تعيين كلمة المرور."
                    badge="منصة الطالب"
                    title={step === 1 ? 'نسيت كلمة المرور' : 'تعيين كلمة مرور جديدة'}
                    footer={(
                        <Box onClick={() => navigate('/login')} className="cursor-pointer hover:underline mx-auto">
                            العودة لتسجيل الدخول
                        </Box>
                    )}
                >
                    {step === 1 ? (
                        <>
                            <TextField
                                onChange={(e) => setEmail(e.target.value)}
                                value={email}
                                label="Email"
                                variant="standard"
                                sx={loginInputSx}
                                slotProps={{ htmlInput: { className: 'py-5' } }}
                            />
                            <button type="button" onClick={forgotPassword} className="auth-btn-primary">
                                {sendWait ? <CircularProgress size={20} color="inherit" /> : 'إرسال الرمز'}
                            </button>
                        </>
                    ) : (
                        <>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2, textAlign: 'center' }}>
                                أدخل الرمز المرسل إلى {email}
                            </Typography>
                            <OTPInput
                                value={code}
                                onChange={setCode}
                                numInputs={6}
                                inputStyle={{
                                    width: '2.75rem',
                                    height: '2.75rem',
                                    margin: '0 0.35rem',
                                    fontSize: '1.25rem',
                                    borderRadius: '8px',
                                    border: '1px solid #ccc',
                                    background: '#fff',
                                }}
                                renderInput={(props) => <input {...props} />}
                            />
                            <TextField
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-6"
                                label="كلمة المرور الجديدة"
                                variant="standard"
                                sx={loginInputSx}
                            />
                            <TextField
                                type="password"
                                value={passwordConfirmation}
                                onChange={(e) => setPasswordConfirmation(e.target.value)}
                                className="mt-4"
                                label="تأكيد كلمة المرور"
                                variant="standard"
                                sx={loginInputSx}
                            />
                            <button type="button" onClick={resetPassword} className="auth-btn-primary">
                                {sendWait ? <CircularProgress size={20} color="inherit" /> : 'تحديث كلمة المرور'}
                            </button>
                        </>
                    )}
                </AuthPageLayout>
            )}
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default ForgotPassword;
